// src/contexts/TeamContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/api';

const TeamContext = createContext();

// Helper pour vérifier si l'utilisateur a un token
const hasAuthToken = () => {
  return !!(localStorage.getItem('token') || localStorage.getItem('pat'));
};

export const TeamProvider = ({ children }) => {
  const [currentTeam, setCurrentTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Fonction pour récupérer le profil de l'utilisateur
  const fetchUserProfile = async () => {
    // Ne rien faire si pas de token (évite les erreurs console)
    if (!hasAuthToken()) {
      return;
    }
    
    try {
      const response = await authenticatedFetch('/profile');
      if (response) {
        setCurrentUser(response);
      }
    } catch (error) {
      // Ignorer silencieusement les erreurs d'authentification
      if (!error.message?.includes('No authentication token')) {
        console.error('Error fetching user profile:', error);
      }
    }
  };

  // Fonction pour restaurer le contexte depuis le localStorage
  const restoreContext = async () => {
    // Ne rien faire si pas de token (évite les erreurs console)
    if (!hasAuthToken()) {
      setIsInitialized(true);
      return;
    }
    
    const contextType = localStorage.getItem('contextType') || 'personal';
    const savedTeamId = localStorage.getItem('currentTeam');
    
    try {
      const response = await authenticatedFetch('/teams');
      const fetchedTeams = response.teams || [];
      setTeams(fetchedTeams);
      
      if (contextType === 'team' && savedTeamId) {
        const team = fetchedTeams.find(t => t.team_id === savedTeamId);
        if (team) {
          setCurrentTeam(team);
        } else {
          localStorage.removeItem('currentTeam');
          localStorage.setItem('contextType', 'personal');
          setCurrentTeam(null);
        }
      } else {
        localStorage.setItem('contextType', 'personal');
        setCurrentTeam(null);
      }
    } catch (error) {
      // Ignorer silencieusement les erreurs d'authentification
      if (!error.message?.includes('No authentication token')) {
        console.error('Error restoring context:', error);
      }
      localStorage.setItem('contextType', 'personal');
      setCurrentTeam(null);
    } finally {
      setIsInitialized(true);
    }
  };

  // Initialisation au chargement de l'application
  useEffect(() => {
    const init = async () => {
      await fetchUserProfile();
      await restoreContext();
    };
    init();
  }, []);

  const fetchTeams = async () => {
    // Ne rien faire si pas de token
    if (!hasAuthToken()) {
      return;
    }
    
    try {
      const response = await authenticatedFetch('/teams');
      const fetchedTeams = response.teams || [];
      setTeams(fetchedTeams);
      
      // Vérifier si l'équipe actuelle existe toujours
      if (currentTeam && !fetchedTeams.find(t => t.team_id === currentTeam.team_id)) {
        setCurrentTeam(null);
        localStorage.removeItem('currentTeam');
        localStorage.setItem('contextType', 'personal');
      }
    } catch (error) {
      if (!error.message?.includes('No authentication token')) {
        console.error('Error fetching teams:', error);
      }
      setTeams([]);
    }
  };

  const fetchPendingInvitations = async () => {
    // Ne rien faire si pas de token
    if (!hasAuthToken()) {
      return;
    }
    
    try {
      const response = await authenticatedFetch('/teams/invitations');
      setPendingInvitations(response.invitations || []);
    } catch (error) {
      // Ignorer silencieusement les erreurs d'authentification
      if (!error.message?.includes('No authentication token')) {
        console.error('Error fetching invitations:', error);
      }
      setPendingInvitations([]);
    }
  };

  useEffect(() => {
    if (isInitialized && hasAuthToken()) {
      fetchPendingInvitations();
    }
  }, [isInitialized]);

  const switchTeam = async (teamId) => {
    const team = teamId ? teams.find(t => t.team_id === teamId) : null;
    setCurrentTeam(team);
    if (team) {
      localStorage.setItem('currentTeam', teamId);
      localStorage.setItem('contextType', 'team');
    } else {
      localStorage.removeItem('currentTeam');
      localStorage.setItem('contextType', 'personal');
    }
    
    // Forcer un rechargement des données après le changement de contexte
    await fetchTeams();
  };

  const leaveTeam = async (teamId) => {
    try {
      await authenticatedFetch(`/teams/${teamId}/leave`, {
        method: 'POST',
      });
      
      if (currentTeam?.team_id === teamId) {
        setCurrentTeam(null);
        localStorage.removeItem('currentTeam');
        localStorage.setItem('contextType', 'personal');
      }
      
      await fetchTeams();
      return true;
    } catch (error) {
      console.error('Error leaving team:', error);
      return false;
    }
  };

  const deleteTeam = async (teamId) => {
    try {
      await authenticatedFetch(`/teams/${teamId}`, {
        method: 'DELETE',
      });
      
      if (currentTeam?.team_id === teamId) {
        setCurrentTeam(null);
        localStorage.removeItem('currentTeam');
        localStorage.setItem('contextType', 'personal');
      }
      
      await fetchTeams();
      return true;
    } catch (error) {
      console.error('Error deleting team:', error);
      return false;
    }
  };

  const respondToInvitation = async (invitationId, accept) => {
    try {
      await authenticatedFetch(`/teams/invitations/${invitationId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ accept }),
      });
      
      await fetchPendingInvitations();
      if (accept) {
        await fetchTeams();
      }
      return true;
    } catch (error) {
      console.error('Error responding to invitation:', error);
      return false;
    }
  };

  const hasWritePermission = () => {
    // Dans le contexte personnel, toujours true
    if (!currentTeam) return true;
    // Dans le contexte équipe, false si viewer
    return currentTeam.role !== 'viewer';
  };

  return (
    <TeamContext.Provider value={{ 
      currentTeam, 
      teams, 
      pendingInvitations,
      switchTeam, 
      fetchTeams,
      fetchPendingInvitations,
      leaveTeam,
      deleteTeam,
      respondToInvitation,
      isInitialized,
      hasWritePermission,
      role: currentTeam?.role || 'owner',
      currentUser
    }}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => useContext(TeamContext);